/**
 * Platform-agnostic RFC 4122 v4 UUID generator.
 *
 * Prefers `crypto.randomUUID()` when the runtime ships it — that's
 * cryptographically secure in modern browsers (Web Crypto) and Node
 * 19+. Falls back to a `Math.random`-based v4 generator for runtimes
 * where it isn't defined: Hermes / React Native does not currently
 * implement `crypto.randomUUID`, and a bare `crypto.randomUUID()`
 * call throws "is not a function (it is undefined)" on the device.
 *
 * Math.random isn't cryptographically secure, but for client-side
 * idempotency keys and local-ID generation that's fine: ~122 bits of
 * entropy means collision-free across billions of requests, and the
 * keys are scoped per-user on the server.
 *
 * Mobile's `apps/mobile/src/lib/uuid.ts` shipped an identical
 * fallback before this helper existed. Prefer this one going forward
 * so the same logic runs on both platforms.
 */
export function randomUuid(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
